import React, { useContext } from "react"
import { get } from "lodash"
import { v4 as uuid } from "uuid"
import { A } from "@4cplatform/elements/Typography"
import { LabelWithText, IconWithText } from "@4cplatform/elements/Atoms"
import { useTranslations } from "@4cplatform/elements/Translations"
import { usePost } from "@4cplatform/elements/Api/useMutation"
import { addAlert } from "@4cplatform/elements/Alerts"

// Helpers
import { PoliciesContext } from "./policies.context"
import { ALinkDummy } from "./policies.styles"

const Documents = () => {
  const { viewLoading, viewData } = useContext(PoliciesContext)
  const t = useTranslations()
  const pmcFiles = get(viewData, "pmc_files", [])
  const ipidLink = get(viewData, "ipid_link", "")
  const hospitalListLink = get(viewData, "hospital_list_link", "")
  const companyThirdPartyPayerFiles = get(viewData, "company_third_party_payer_files", [])
  const canGenerateApplicationform =
    [
      "AWAITING_TERMS",
      "AWAITING_ACCEPTANCE",
      "ACCEPTED",
      "FAILED_ONBOARDING",
      "DECLINED",
      "DECLINED_UNDERWRITING"
    ].includes(get(viewData, "status", [])) &&
    get(viewData, "provider.provider_key", "") !== "VITALITYHEALTH"

  // Generate Application Form
  const [generateApplicationFormDocument, { loading: generateApplicationFormDocumentLoading }] =
    usePost({
      endpoint: "/policies/:policy/generate-document/application_form",
      params: {
        policy: get(viewData, "slug", "")
      },
      onCompleted: res => {
        const filePath = get(res, "data.path", "")
        window.open(filePath, "_blank")
      },
      onError: () => {
        addAlert({
          type: "error",
          message: t("ONBOARDED_DOCUMENTATION_GENERATE_ERROR"),
          dismissible: true,
          timeout: 5
        })
      }
    })

  // Generate Brochure
  const [generateBrochure, { loading: generateBrochureDocumentLoading }] = usePost({
    endpoint: "/policies/:policy/generate-document/brochure",
    params: {
      policy: get(viewData, "slug", "")
    },
    onCompleted: res => {
      const filePath = get(res, "data.path", "")
      window.open(filePath, "_blank")
    },
    onError: () => {
      addAlert({
        type: "error",
        message: t("ONBOARDED_DOCUMENTATION_GENERATE_ERROR"),
        dismissible: true,
        timeout: 5
      })
    }
  })

  // Generate Demands & Needs
  const [generateDemandsNeedsDocument, { loading: generateDemandsAndNeedsDocumentLoading }] =
    usePost({
      endpoint: "/policies/:policy/generate-document/demands_and_needs",
      params: {
        policy: get(viewData, "slug", "")
      },
      onCompleted: res => {
        const filePath = get(res, "data.path", "")
        window.open(filePath, "_blank")
      },
      onError: () => {
        addAlert({
          type: "error",
          message: t("ONBOARDED_DOCUMENTATION_GENERATE_ERROR"),
          dismissible: true,
          timeout: 5
        })
      }
    })

  const applicationForm = () =>
    generateApplicationFormDocumentLoading ? (
      <ALinkDummy margin="0" colour="white" isLoading={generateApplicationFormDocumentLoading}>
        Fetching
      </ALinkDummy>
    ) : (
      <A
        appearance="light"
        fontSize="1.6rem"
        onClick={() => generateApplicationFormDocument()}
        isLoading={generateApplicationFormDocumentLoading}
      >
        Application Form
      </A>
    )

  const brochure = () =>
    generateBrochureDocumentLoading ? (
      <ALinkDummy margin="0" colour="white" isLoading={generateBrochureDocumentLoading}>
        Fetching
      </ALinkDummy>
    ) : (
      <A
        appearance="light"
        fontSize="1.6rem"
        onClick={() => generateBrochure()}
        isLoading={generateBrochureDocumentLoading}
      >
        Brochure
      </A>
    )

  const demandsAndNeeds = () =>
    generateDemandsAndNeedsDocumentLoading ? (
      <ALinkDummy margin="0" colour="white" isLoading={generateDemandsAndNeedsDocumentLoading}>
        Fetching
      </ALinkDummy>
    ) : (
      <A
        appearance="light"
        fontSize="1.6rem"
        onClick={() => generateDemandsNeedsDocument()}
        isLoading={generateDemandsAndNeedsDocumentLoading}
      >
        Demands & Needs
      </A>
    )

  return (
    <>
      <LabelWithText label="Documents" appearance="light" isLoading={viewLoading}>
        {canGenerateApplicationform && (
          <IconWithText
            key={uuid()}
            appearance="light"
            icon="filedocument"
            isLoading={viewLoading}
            margin="0"
          >
            {applicationForm()}
          </IconWithText>
        )}

        <IconWithText
          key={uuid()}
          appearance="light"
          icon="filedocument"
          isLoading={viewLoading}
          margin="0"
        >
          {brochure()}
        </IconWithText>

        <IconWithText
          key={uuid()}
          appearance="light"
          icon="filedocument"
          isLoading={viewLoading}
          margin="0"
        >
          {demandsAndNeeds()}
        </IconWithText>

        {pmcFiles.map(document => (
          <IconWithText
            key={uuid()}
            appearance="light"
            icon="filedocument"
            isLoading={viewLoading}
            margin="0"
          >
            <A
              appearance="light"
              fontSize="1.6rem"
              href={document.url}
              rel="noopener noreferrer"
              target="_blank"
              download
            >
              {document.file_name}
            </A>
          </IconWithText>
        ))}

        {ipidLink && (
          <IconWithText
            key={uuid()}
            appearance="light"
            icon="filedocument"
            isLoading={viewLoading}
            margin="0"
          >
            <A appearance="light" fontSize="1.6rem" href={ipidLink} target="_blank">
              IPID
            </A>
          </IconWithText>
        )}

        {hospitalListLink && (
          <IconWithText
            key={uuid()}
            appearance="light"
            icon="filedocument"
            isLoading={viewLoading}
            margin="0"
          >
            <A appearance="light" fontSize="1.6rem" href={hospitalListLink} target="_blank">
              Hospital List
            </A>
          </IconWithText>
        )}

        {companyThirdPartyPayerFiles &&
          companyThirdPartyPayerFiles.map(document => (
            <IconWithText
              key={uuid()}
              appearance="light"
              icon="filedocument"
              isLoading={viewLoading}
              margin="0"
            >
              <A appearance="light" fontSize="1.6rem" href={document.url} target="_blank">
                {document.file_name}
              </A>
            </IconWithText>
          ))}
      </LabelWithText>
    </>
  )
}

export default Documents
